import { Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useState } from 'react';

import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';

import { CategoriesTable } from './components/CategoriesTable';
import { CategoryEditorDialog } from './components/CategoryEditorDialog';
import { CategoryVersionHistory } from './components/CategoryVersionHistory';
import {
  useCreateIssueCategory,
  useIssueCategories,
  useIssueCategoryVersions,
  useUpdateIssueCategory,
} from './online-eval.query';

import type { IssueCategory, IssueCategoryInput } from './online-eval.types';

export const OnlineEvalCategoriesPage = () => {
  const toast = useToast();
  const categoriesQuery = useIssueCategories();
  const versionsQuery = useIssueCategoryVersions();
  const currentVersion = versionsQuery.data?.[0];
  const createCategory = useCreateIssueCategory();
  const updateCategory = useUpdateIssueCategory();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<IssueCategory | null>(null);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (category: IssueCategory) => {
    setEditing(category);
    setDialogOpen(true);
  };
  const save = (input: IssueCategoryInput) => {
    const options = { onSuccess: () => setDialogOpen(false) };
    if (editing) {
      updateCategory.mutate({ id: editing.id, input }, options);
    } else {
      createCategory.mutate(input, options);
    }
  };
  const activeMutation = editing ? updateCategory : createCategory;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-primary">Online eval</p>
          <h1 className="text-3xl font-semibold tracking-tight">
            Issue categories
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Define the issue taxonomy used by the evaluator. Categories cannot
            be deleted because historical findings reference them. Deprecate one
            instead: new runs stop flagging it, and the findings it already has
            stay visible. A deprecated category has to be reactivated before it
            can be edited.
          </p>
          {currentVersion && (
            <p className="mt-2 text-sm font-medium">
              Current version: {currentVersion.id}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to="/online-eval/issues">View flagged issues</Link>
          </Button>
          <Button onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" /> New category
          </Button>
        </div>
      </div>

      {categoriesQuery.isLoading ? (
        <div className="py-16 text-center text-sm text-muted-foreground">
          Loading categories…
        </div>
      ) : categoriesQuery.error ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          {categoriesQuery.error.message}
        </div>
      ) : (
        <CategoriesTable
          categories={categoriesQuery.data ?? []}
          onEdit={openEdit}
          onDeprecatedChange={(category, deprecated) =>
            updateCategory.mutate(
              { id: category.id, input: { deprecated } },
              { onError: (error) => toast.error(error.message) }
            )
          }
        />
      )}

      {versionsQuery.data && (
        <CategoryVersionHistory versions={versionsQuery.data} />
      )}

      <CategoryEditorDialog
        open={dialogOpen}
        category={editing}
        pending={activeMutation.isPending}
        error={activeMutation.error?.message ?? null}
        onOpenChange={setDialogOpen}
        onSave={save}
      />
    </div>
  );
};
